import React, {useEffect, useRef, useState} from "react"

import {useInputContext} from "../useInputContext"

const TAB_KEY_CODE = "Tab"
const FOCUS_VISIBLE_ATTR = "data-focus-visible-added"
const INDEX_ATTR_KEY = "data-tab-index"
const TABBABLE_ELEMENTS_QUERY_STRING_DEFAULT =
    'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'

// Object type to allow extending the API for further use cases
type CustomTabOrderOptions = {
    elementOrder: React.RefObject<HTMLElement | null>[]
}

const getIndexFromAttr = (el: HTMLElement): number => {
    return Number.parseInt(el.getAttribute(INDEX_ATTR_KEY) || "NaN", 10)
}

const shiftFocus = (oldEl: HTMLElement, newEl: HTMLElement) => {
    oldEl.removeAttribute(FOCUS_VISIBLE_ATTR)
    newEl.setAttribute(FOCUS_VISIBLE_ATTR, "")
    newEl.focus()
}

const getSelectableDOMElements = (customQuerySelector?: string) => {
    return Array.from(
        document.querySelectorAll<HTMLElement>(customQuerySelector || TABBABLE_ELEMENTS_QUERY_STRING_DEFAULT),
    ).filter(el => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"))
}

export const useCustomTabOrder = (opts: CustomTabOrderOptions) => {
    const [capturedTab, setCapturedTab] = useState(false)
    const mountedRefs = useRef<HTMLElement[]>([])
    const {lastInput, lastInputShiftPressed} = useInputContext()

    useEffect(() => {
        const keydownHandler = (ev: KeyboardEvent) => {
            if (ev.key === TAB_KEY_CODE && capturedTab) {
                const el = ev.target as HTMLElement
                const tabIdx = getIndexFromAttr(el)

                if (
                    mountedRefs.current.length > 1 &&
                    !((tabIdx === 0 && ev.shiftKey) || (tabIdx === mountedRefs.current.length - 1 && !ev.shiftKey))
                ) {
                    ev.preventDefault()
                    shiftFocus(el, mountedRefs.current[tabIdx + (ev.shiftKey ? -1 : 1)])
                } else {
                    // Exit custom tab order
                    setCapturedTab(false)
                }
            }
        }

        // Tabbed into an element with custom tab order - if first or last element and tab/shift-tabbing respectively then take control of tab order
        const focusHandler = (ev: FocusEvent) => {
            // if there's no Clear button, we don't need to do any custom focus management -- everything's in logical order already
            if (!opts.elementOrder[1].current) {
                return
            }

            const el = ev.target as HTMLElement
            const tabIdx = getIndexFromAttr(el)

            // We don't need to investigate the focus event further if there's only 1 mounted element in the tab order
            if (mountedRefs.current.length > 1 && !capturedTab) {
                if (
                    ((tabIdx === 0 && lastInputShiftPressed) ||
                        (tabIdx === mountedRefs.current.length - 1 && !lastInputShiftPressed) ||
                        (tabIdx !== 0 && tabIdx !== mountedRefs.current.length - 1)) &&
                    lastInput === "keyboard"
                ) {
                    // Element is in the middle of the array - tab to the next element and skip this one
                    ev.preventDefault()
                    const tababbleHierarchy = getSelectableDOMElements()
                    const indexInHierarchy = tababbleHierarchy.indexOf(el)
                    shiftFocus(el, tababbleHierarchy[indexInHierarchy + (lastInputShiftPressed ? -1 : 1)])
                } else if (lastInput === "mouse") {
                    // Mouse events don't trigger focus-visible polyfill - only capture tab to cause next tab event to progress through custom order
                    setCapturedTab(true)
                } else {
                    // Attach to custom tab order
                    setCapturedTab(true)
                    shiftFocus(
                        document.querySelector(`* ${FOCUS_VISIBLE_ATTR}`) || document.createElement("div"),
                        mountedRefs.current[lastInputShiftPressed ? mountedRefs.current.length - 1 : 0],
                    )
                }
            }
        }

        opts.elementOrder.forEach(ref => {
            // Setup elements from refs into metaref
            if (ref.current) {
                const element = ref.current
                element.setAttribute(INDEX_ATTR_KEY, `${mountedRefs.current.length}`)
                element.addEventListener("keydown", keydownHandler)
                element.addEventListener("focus", focusHandler)
                mountedRefs.current.push(element)
            }
        })

        return () => {
            // Destroy hook listeners
            mountedRefs.current.forEach(elRef => {
                const el = elRef || document.createElement("div")
                el.removeAttribute(INDEX_ATTR_KEY)
                el.removeEventListener("keydown", keydownHandler)
                el.removeEventListener("focus", focusHandler)
            })
            mountedRefs.current = []
        }
    }, [opts, capturedTab, lastInput, lastInputShiftPressed])
}
